import { useTranslation } from 'react-i18next';
import { Column, ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import ArrowDownIconSolid from '@/components/icons/ArrowDownIconSolid';
import { UserListItemResponseDto } from '@/features/superAdmin/features/users/types/superAdmin-users-dto';
import { useUsersFilter } from '@/features/superAdmin/features/users/hooks/useUsersFilter';

const UsersPageColumns = (): ColumnDef<UserListItemResponseDto>[] => {
	const { t } = useTranslation();

	const { setFilter } = useUsersFilter();

	const handleSort = (
		columnKey: Capitalize<keyof UserListItemResponseDto>,
		column: Column<UserListItemResponseDto>,
	) => {
		const newDirection = column.getIsSorted() === 'asc';
		column.toggleSorting(newDirection);

		setFilter({
			orderBy: columnKey,
			orderDirection: newDirection ? 'asc' : 'desc',
		});
	};

	return [
		{
			accessorKey: 'id',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Id', column)}>
						{t('main.superAdmin.users.table.header.id')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'text-sm text-popover-800'}>
					{row.getValue('id')}
				</div>
			),
		},
		{
			accessorKey: 'userName',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('UserName', column)}>
						{t('main.superAdmin.users.table.header.name')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'flex items-center gap-2.5'}>
					<h4 className='text-sm text-popover-800 truncate'>
						{row.getValue('userName')}
					</h4>
				</div>
			),
		},
		{
			accessorKey: 'email',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Email', column)}>
						{t('main.superAdmin.users.table.header.email')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'text-sm text-popover-800'}>
					<h4 className='text-sm text-popover-800 truncate'>
						{row.getValue('email')}
					</h4>
				</div>
			),
		},
		{
			id: 'assignedTenants',
			header: () => (
				<span className='text-sm text-popover-1000 font-medium'>
					{t('main.superAdmin.users.table.header.tenants')}
				</span>
			),
			cell: ({ row }) => {
				const list = row.original.assignedTenants ?? [];
				const text = list
					.map((x) => (x.name?.trim() ? x.name : x.tenantId))
					.join(', ');
				return (
					<div
						className='text-sm text-popover-800 max-w-xs truncate'
						title={text || undefined}>
						{text || '—'}
					</div>
				);
			},
		},
	];
};

export default UsersPageColumns;
